import { Dropdown, Flex, Stack, Text, useDropdownState } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'

import { useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { type ItemRestriction, OPERATORS } from 'typings'
import { useFormStateContext } from '../../../providers'
import {
  FormAliasKeys,
  useAddFormAlias,
} from '../../../providers/useFormAlias.constate'
import { SalesChannelsSelector } from './SalesChannelsSelector'

type Props = {
  name: string
}

export function SalesChannels(props: Props) {
  const { name } = props
  const { formatMessage } = useIntl()
  const { form } = useFormStateContext()

  const { addAlias } = useAddFormAlias()

  const salesChannelRestriction: ItemRestriction | undefined = useWatch({
    form,
    name,
  })

  const currentOperator = useMemo(
    () => salesChannelRestriction?.operator ?? OPERATORS.ALL,
    [salesChannelRestriction?.operator]
  )

  const operatorOptions = [
    {
      id: OPERATORS.ALL,
      label: formatMessage({ id: 'admin/promotions.form.sales-channels.all' }),
    },
    {
      id: OPERATORS.EQUALS,
      label: formatMessage({
        id: 'admin/promotions.form.sales-channels.equals',
      }),
    },
    {
      id: OPERATORS.DIFFERENT,
      label: formatMessage({
        id: 'admin/promotions.form.sales-channels.different',
      }),
    },
  ]

  const operatorState = useDropdownState({
    items: operatorOptions,
    itemToString: (item) => item?.id ?? '',
    initialSelectedItem: operatorOptions.find((o) => o.id === currentOperator),
    onSelectedItemChange: (event) => {
      const selectedValue = event.selectedItem?.id
      const items = form.getValues(`${name}.items`)
      if (
        selectedValue === OPERATORS.EQUALS ||
        selectedValue === OPERATORS.DIFFERENT
      ) {
        form.setValue(name, {
          operator: selectedValue,
          items: items ?? [],
        })
      } else {
        form.setValue(name, undefined)
      }
    },
  })

  useEffect(() => {
    addAlias({
      value: name,
      key: FormAliasKeys.salesChannels,
    })
  }, [addAlias, name])

  return (
    <Stack space="$space-5" fluid>
      <Flex align="center" justify="space-between">
        <Text variant="title1">
          <FormattedMessage id="admin/promotions.form.sales-channels.title" />
        </Text>
        <Dropdown
          items={operatorOptions}
          state={operatorState}
          label={formatMessage({
            id: 'admin/promotions.form.sales-channels.operator',
          })}
          variant="neutralTertiary"
          renderItem={(item) => item?.label ?? ''}
        />
      </Flex>
      {currentOperator !== OPERATORS.ALL && (
        <SalesChannelsSelector name={`${name}.items`} />
      )}
    </Stack>
  )
}
